/**
 * Wandelt einen beliebigen Fehler-Wert in eine saubere, menschenlesbare
 * Meldung um. Verhindert, dass jemals „{}", „[object Object]", „undefined"
 * oder Roh-JSON in einer Fehler-Box landet.
 *
 * Hintergrund: Supabase/Fetch können bei Server-Problemen (z. B. ein 503
 * mit einer nicht-JSON-Antwort vom Gateway) Fehler zurückgeben, deren
 * `.message` leer ist oder die zu einem leeren Objekt serialisieren. Ohne
 * diese Normalisierung sah der Nutzer dann „{}" im roten Kästchen.
 */

// Werte, die zwar Strings sind, aber KEINE echte Meldung darstellen.
const GARBAGE = new Set(["{}", "[object object]", "undefined", "null", ""]);

export function toErrorMessage(err: unknown, fallback: string): string {
  const clean = (s: unknown): string | null => {
    if (typeof s !== "string") return null;
    const t = s.trim();
    if (!t || GARBAGE.has(t.toLowerCase())) return null;
    // Rohe Gateway-/Proxy-Fehler (Envoy etc.) sind für Nutzer wertlos.
    if (
      t.startsWith("upstream connect error") ||
      t.includes("<!DOCTYPE") ||
      t.startsWith("<html")
    ) {
      return null;
    }
    return t;
  };

  // 1) Direkter String
  const direct = clean(err);
  if (direct) return direct;

  // 2) Error-Instanz
  if (err instanceof Error) {
    const m = clean(err.message);
    if (m) return m;
  }

  // 3) Objekt mit .message oder .error
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    const m = clean(o.message) ?? clean(o.error);
    if (m) return m;
    // Verschachtelt: { error: { message } }
    if (o.error && typeof o.error === "object") {
      const nested = clean((o.error as Record<string, unknown>).message);
      if (nested) return nested;
    }
  }

  // 4) Nichts Brauchbares → Fallback
  return fallback;
}

/**
 * Erkennt, ob ein Fehler auf ein temporäres Server-/Netzwerkproblem
 * hindeutet (Auth-Dienst nicht erreichbar, 5xx, Fetch-Abbruch). Dann
 * zeigen wir eine gezielte „gleich nochmal versuchen"-Meldung statt einer
 * technischen Rohmeldung.
 */
export function isTransientServerError(err: unknown): boolean {
  const s = (
    err instanceof Error ? err.message : typeof err === "string" ? err : ""
  ).toLowerCase();
  const status = (err as { status?: number })?.status;
  return (
    status === 503 ||
    status === 502 ||
    status === 504 ||
    s.includes("upstream connect error") ||
    s.includes("failed to fetch") ||
    s.includes("networkerror") ||
    s.includes("load failed") ||
    s.includes("fetch failed")
  );
}
